import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Chip, Collapse, IconButton, Box, Button, Stack, useTheme } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TimelineIcon from '@mui/icons-material/Timeline';
import GroupIcon from '@mui/icons-material/Group';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SeriesChart from '../charts/SeriesChart';
import EquipesAderenciaModal from './EquipesAderenciaModal';
import TurnosRankingModal from './TurnosRankingModal';
import {
  FONT_TITLE,
  FONT_HEADER,
  FONT_BODY,
  FONT_SUBTITLE,
  COLOR_HEADER_CONTRATO,
  COLOR_HEADER_CENTRO,
  MARGIN_LEFT,
  MARGIN_RIGHT
} from '../../utils/pdfStyles';

const getRealizadoColor = (valor) => {
  if (valor >= 100) return 'success';
  if (valor >= 80) return 'warning';
  return 'error';
};

const DetalheCentroTable = ({ centros, isDark }) => {
  const rowBg = isDark ? 'rgba(255, 255, 255, 0.04)' : '#fafbfc';
  const sortedCentros = [...centros].sort((a, b) => {
    const aVal = typeof a.percentual_realizado === 'number' ? a.percentual_realizado : -99999;
    const bVal = typeof b.percentual_realizado === 'number' ? b.percentual_realizado : -99999;
    return bVal - aVal;
  });
  return (
    <Table size="medium" sx={{ boxShadow: 0, margin: 0 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 56 }} />
          <TableCell sx={{ fontWeight: 700, width: 350 }}>Centro de Serviço</TableCell>
          <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 160 }} align="center">Turnos Previstos</TableCell>
          <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 160 }} align="center">Turnos Abertos</TableCell>
          <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 160 }} align="center">% Realizado</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedCentros.map((centro, idx) => (
          <TableRow key={centro.des_centro_servico || idx} sx={{ backgroundColor: idx % 2 === 0 ? rowBg : 'transparent' }}>
            <TableCell sx={{ width: 56 }} />
            <TableCell sx={{ width: 350 }}>{centro.des_centro_servico}</TableCell>
            <TableCell align="center" sx={{ width: 160 }}>{centro.total_previsto ?? '--'}</TableCell>
            <TableCell align="center" sx={{ width: 160 }}>{centro.total_aberto ?? '--'}</TableCell>
            <TableCell align="center" sx={{ width: 160 }}>
              <Chip
                label={typeof centro.percentual_realizado === 'number' ? `${centro.percentual_realizado.toFixed(2)}%` : '--'}
                color={getRealizadoColor(centro.percentual_realizado)}
                size="small"
                sx={{ fontWeight: 600 }}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const TurnosContratosTable = ({ data = [], startDate, endDate, contrato = 'Todos', centro = 'Todos', equipe = 'Todos' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const headerBg = isDark ? '#153444' : 'grey.100';
  const rowBgEven = isDark ? 'rgba(255, 255, 255, 0.04)' : 'background.default';
  const rowBgOdd = isDark ? 'rgba(255, 255, 255, 0.08)' : 'grey.50';
  const [openRows, setOpenRows] = useState({});
  const [detalhes, setDetalhes] = useState({});
  const [loadingDetalhe, setLoadingDetalhe] = useState({});
  const [exportando, setExportando] = useState(false);
  const [seriesChartOpen, setSeriesChartOpen] = useState(false);
  const [equipesAderenciaOpen, setEquipesAderenciaOpen] = useState(false);
  const [rankingOpen, setRankingOpen] = useState(false);

  const sorted = [...data].sort((a, b) => {
    const aVal = typeof a.percentual_realizado === 'number' ? a.percentual_realizado : -99999;
    const bVal = typeof b.percentual_realizado === 'number' ? b.percentual_realizado : -99999;
    return bVal - aVal;
  });

  const handleExpand = async (contrato) => {
    setOpenRows((prev) => ({ ...prev, [contrato]: !prev[contrato] }));
    if (!detalhes[contrato] && !loadingDetalhe[contrato]) {
      setLoadingDetalhe((prev) => ({ ...prev, [contrato]: true }));
      try {
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const formatDate = (date) => {
          let d = date;
          if (!d) d = new Date();
          else if (typeof d === 'string' || typeof d === 'number') d = new Date(d);
          if (!(d instanceof Date) || isNaN(d.getTime())) d = new Date();
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        };
        const payload = {
          'data-referencia-inicio': formatDate(startDate),
          'data-referencia-fim': formatDate(endDate),
          'des_contrato': contrato,
          'des_centro_servico': centro,
          'des_equipe': equipe
        };
        const resp = await fetch(`${apiUrl}/percentual-turnos-centro-servico`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await resp.json();
        setDetalhes((prev) => ({ ...prev, [contrato]: Array.isArray(data) ? data : [] }));
      } catch {
        setDetalhes((prev) => ({ ...prev, [contrato]: [] }));
      } finally {
        setLoadingDetalhe((prev) => ({ ...prev, [contrato]: false }));
      }
    }
  };

  const exportToPDF = async (sorted) => {
    setExportando(true);
    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const detalhesAll = {};
    const formatDate = (date) => {
      let d = date;
      if (!d) d = new Date();
      else if (typeof d === 'string' || typeof d === 'number') d = new Date(d);
      if (!(d instanceof Date) || isNaN(d.getTime())) d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };
    await Promise.all(sorted.map(async (row) => {
      try {
        const payload = {
          'data-referencia-inicio': formatDate(startDate),
          'data-referencia-fim': formatDate(endDate),
          'des_contrato': row.des_contrato,
          'des_centro_servico': centro,
          'des_equipe': equipe
        };
        const resp = await fetch(`${apiUrl}/percentual-turnos-centro-servico`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await resp.json();
        detalhesAll[row.des_contrato] = Array.isArray(data) ? data : [];
      } catch {
        detalhesAll[row.des_contrato] = [];
      }
    }));
    const doc = new jsPDF();
    doc.setFontSize(FONT_TITLE);
    doc.text('Turnos Previstos/Abertos/% Realizado', MARGIN_LEFT, 18);
    // Tabela principal
    const mainTableBody = sorted.map((row, idx) => {
      let rank = idx + 1;
      return [
        { content: rank, styles: { fontWeight: 'bold', fontSize: FONT_BODY } },
        row.des_contrato,
        row.total_previsto ?? '--',
        row.total_aberto ?? '--',
        typeof row.percentual_realizado === 'number' ? row.percentual_realizado.toFixed(2) + '%' : '--'
      ];
    });
    autoTable(doc, {
      startY: 24,
      head: [['Ranking', 'Contrato', 'Turnos Previstos', 'Turnos Abertos', '% Realizado']],
      body: mainTableBody,
      theme: 'grid',
      styles: { fontSize: FONT_BODY },
      headStyles: { fillColor: COLOR_HEADER_CONTRATO, fontSize: FONT_HEADER },
      margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT },
      didDrawCell: (data) => {
        if (data.row.index < sorted.length - 1) {
          data.cell.height += 2;
        }
      }
    });
    let y = doc.lastAutoTable.finalY + 4;
    // Sub-tabelas de centros de serviço
    sorted.forEach((row, idx) => {
      const centros = detalhesAll[row.des_contrato] || [];
      doc.setFontSize(FONT_SUBTITLE);
      doc.text(`Contrato: ${row.des_contrato} - Centros de Serviço`, MARGIN_LEFT, y);
      y += 4;
      if (centros.length > 0) {
        const centrosOrdenados = [...centros].sort((a, b) => {
          const aVal = typeof a.percentual_realizado === 'number' ? a.percentual_realizado : -99999;
          const bVal = typeof b.percentual_realizado === 'number' ? b.percentual_realizado : -99999;
          return bVal - aVal;
        });
        autoTable(doc, {
          startY: y,
          head: [[
            'Centro de Serviço', 'Turnos Previstos', 'Turnos Abertos', '% Realizado'
          ]],
          body: centrosOrdenados.map(centro => [
            centro.des_centro_servico,
            centro.total_previsto ?? '--',
            centro.total_aberto ?? '--',
            typeof centro.percentual_realizado === 'number' ? centro.percentual_realizado.toFixed(2) + '%' : '--'
          ]),
          theme: 'grid',
          styles: { fontSize: FONT_BODY },
          headStyles: { fillColor: COLOR_HEADER_CENTRO, fontSize: FONT_HEADER },
          margin: { left: MARGIN_LEFT, right: MARGIN_RIGHT },
        });
        y = doc.lastAutoTable.finalY + 8;
      } else {
        doc.setFontSize(FONT_BODY);
        doc.text('Sem dados de centros de serviço.', MARGIN_LEFT, y);
        y += 8;
      }
    });
    doc.save('turnos_contratos_ranking.pdf');
    setExportando(false);
  };

  return (
    <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 3, mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Turnos Previstos/Abertos/% Realizado
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<LeaderboardIcon />}
            onClick={() => setRankingOpen(true)}
            sx={{ fontWeight: 600 }}
          >
            Ranking
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<TimelineIcon />}
            onClick={() => setSeriesChartOpen(true)}
            sx={{ fontWeight: 600 }}
          >
            Séries
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<GroupIcon />}
            onClick={() => setEquipesAderenciaOpen(true)}
            sx={{ fontWeight: 600 }}
          >
            Equipes
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => exportToPDF(sorted)}
            sx={{ fontWeight: 600 }}
            disabled={exportando}
          >
            {exportando ? 'Exportando...' : 'Exportar PDF'}
          </Button>
        </Stack>
      </Stack>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: headerBg }}>
            <TableCell sx={{ width: 56 }} />
            <TableCell sx={{ fontWeight: 700, width: 350 }}>Contrato</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 160 }} align="center">Turnos Previstos</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 160 }} align="center">Turnos Abertos</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center', width: 160 }} align="center">% Realizado</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography color="text.secondary">Nenhum dado encontrado.</Typography>
              </TableCell>
            </TableRow>
          ) : (
            sorted.map((row, idx) => (
              <React.Fragment key={row.des_contrato || idx}>
                <TableRow hover sx={{ backgroundColor: idx % 2 === 0 ? rowBgEven : rowBgOdd }}>
                  <TableCell sx={{ width: 56 }}>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => handleExpand(row.des_contrato)}
                    >
                      {openRows[row.des_contrato] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ width: 350 }}>{row.des_contrato}</TableCell>
                  <TableCell align="center" sx={{ width: 160 }}>{row.total_previsto ?? '--'}</TableCell>
                  <TableCell align="center" sx={{ width: 160 }}>{row.total_aberto ?? '--'}</TableCell>
                  <TableCell align="center" sx={{ width: 160 }}>
                    <Chip
                      label={typeof row.percentual_realizado === 'number' ? `${row.percentual_realizado.toFixed(2)}%` : '--'}
                      color={getRealizadoColor(row.percentual_realizado)}
                      size="small"
                      sx={{ fontWeight: 700, fontSize: 15, minWidth: 80 }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                    <Collapse in={openRows[row.des_contrato]} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 0, py: 1 }}>
                        {loadingDetalhe[row.des_contrato] ? (
                          <Typography color="text.secondary">Carregando...</Typography>
                        ) : (
                          <DetalheCentroTable centros={detalhes[row.des_contrato] || []} isDark={isDark} />
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))
          )}
        </TableBody>
      </Table>
      
      <SeriesChart
        open={seriesChartOpen}
        onClose={() => setSeriesChartOpen(false)}
        startDate={startDate}
        endDate={endDate}
        contrato={contrato}
        centro={centro}
        equipe={equipe}
      />
      
      <EquipesAderenciaModal
        open={equipesAderenciaOpen}
        onClose={() => setEquipesAderenciaOpen(false)}
        startDate={startDate}
        endDate={endDate}
        contrato={contrato}
        centro={centro}
        equipe={equipe}
      />

      <TurnosRankingModal
        open={rankingOpen}
        onClose={() => setRankingOpen(false)}
        startDate={startDate}
        endDate={endDate}
        contrato={contrato}
        centro={centro}
        equipe={equipe}
      />
    </TableContainer>
  );
};

export default TurnosContratosTable; 